import { useState } from 'react';
import { WorkflowTab } from '../../components/WorkflowTab';
import { PipelineTab } from '../../form-config/pipeline/PipelineTab';
import type { Surface } from '@capture/shared';

type Section = 'stages' | 'profiles';

interface ConsolidatedPipelineTabProps {
  jobTypeId: string;
  /** Field registry rows WorkflowTab needs for routing-condition editors. */
  fields: Parameters<typeof WorkflowTab>[0]['fields'];
  /** SP3 §2 — threaded to WorkflowTab to gate the remittance step catalog. */
  surface?: Surface;
}

/**
 * One Pipeline tab on Job Config, co-locating the two pipeline surfaces
 * (Spec 2026-06-28 §5, decision A — co-locate, do NOT merge):
 *   - Stages   → WorkflowTab (REST PipelineStep rows; ordered stage skeleton)
 *   - Profiles → PipelineTab (versioned draft profiles; conditional variants)
 * `?tab=pipeline` lands on Stages by default.
 */
export function ConsolidatedPipelineTab({ jobTypeId, fields, surface }: ConsolidatedPipelineTabProps) {
  const [section, setSection] = useState<Section>('stages');
  return (
    <div className="pipeline-consolidated">
      <div className="pipeline-consolidated__subtabs" role="tablist">
        <button role="tab" aria-selected={section === 'stages'} onClick={() => setSection('stages')}>
          Stages
        </button>
        <button role="tab" aria-selected={section === 'profiles'} onClick={() => setSection('profiles')}>
          Profiles
        </button>
      </div>
      {section === 'stages' && (
        <>
          {/* E-044 — Stages writes straight to the live PipelineStep rows via
              REST (WorkflowTab), with no draft/publish gate the way Form
              Config has. A change here is live immediately, including for
              batches already mid-pipeline — say so up front instead of
              letting an operator assume it behaves like the rest of Job
              Config. Profiles (the other half of this tab) DOES have a
              draft, via the Form Config store, so this notice is scoped to
              Stages only. */}
          <p className="jt-dormant-notice jt-dormant-notice--page" role="status">
            Pipeline changes apply immediately, including to in-flight work — there is no draft
            for this surface.
          </p>
          <WorkflowTab jobTypeId={jobTypeId} fields={fields} surface={surface} />
        </>
      )}
      {section === 'profiles' && <PipelineTab />}
    </div>
  );
}
